import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { ArrowIcon } from "../atoms/icon/ArrowIcon";
import { COLOR_PRIMARY } from "../../constants/color";
import { useLanguage } from "../../contexts/LanguageContext";

type Props = {
  minPrice: string;
  maxPrice: string;
  onChange: (field: 'minPrice' | 'maxPrice', value: string) => void;
  placeholder?: string;
  width?: string;
  height?: string;
};

export const PriceRangeSelector: React.FC<Props> = ({
  minPrice,
  maxPrice,
  onChange,
  placeholder = "Price Range",
  width,
  height,
}) => {
  const { t } = useLanguage();
  const uuid = useMemo(() => uuidv4(), []);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenSelection = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const closeDropdown = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest(`#container-${uuid}`)) return;
    setIsOpen(false);
  }, [uuid]);

  useEffect(() => {
    if (isOpen) {
      document.body.addEventListener("click", closeDropdown);
    } else {
      document.body.removeEventListener("click", closeDropdown);
    }
    return () => document.body.removeEventListener("click", closeDropdown);
  }, [isOpen, closeDropdown]);

  const displayValue = useMemo(() => {
    if (minPrice && maxPrice) {
      return `$${minPrice} - $${maxPrice}`;
    } else if (minPrice) {
      return `${t('property.from')} $${minPrice}`;
    } else if (maxPrice) {
      return `${t('property.upTo')} $${maxPrice}`;
    }
    return placeholder;
  }, [minPrice, maxPrice, placeholder, t]);

  return (
    <StyledContainer id={`container-${uuid}`} $width={width || "100%"} $height={height}>
      <StyledHeader />
      <StyledSelect onClick={handleOpenSelection}>
        <StyledPlaceholder $isPlaceholder={!minPrice && !maxPrice}>
          {displayValue}
        </StyledPlaceholder>
        <StyledArrowIcon $isOpen={isOpen}>
          <ArrowIcon uuid={uuid} fillColor="#333333" />
        </StyledArrowIcon>
      </StyledSelect>

      <StyledDropdown $isOpen={isOpen}>
        <InputWrapper>
          <StyledInput
            type="number"
            placeholder={t('property.minPrice')}
            value={minPrice}
            onChange={(e) => onChange('minPrice', e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <StyledInput
            type="number"
            placeholder={t('property.maxPrice')}
            value={maxPrice}
            onChange={(e) => onChange('maxPrice', e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </InputWrapper>
      </StyledDropdown>
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $width: string; $height?: string }>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height || "auto"};
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StyledSelect = styled.div`
  padding: 10px 12px 10px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid #cccccc;
  background-color: #ffffff;
  outline: none;
  box-sizing: border-box;
  min-height: 44px;
  height: 100%;
  cursor: pointer;
`;

const StyledPlaceholder = styled.div<{ $isPlaceholder: boolean }>`
  font-size: 14px;
  font-weight: 400;
  line-height: 170%;
  letter-spacing: 0.56px;
  color: ${({ $isPlaceholder }) => ($isPlaceholder ? "#999999" : "#000000")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const rotate = ($isOpen: boolean) => keyframes`
  from { transform: rotate(${$isOpen ? 0 : 180}deg); }
  to { transform: rotate(${$isOpen ? 180 : 0}deg); }
`;

const StyledArrowIcon = styled.div<{ $isOpen: boolean }>`
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  animation: ${({ $isOpen }) => rotate($isOpen)} 0.2s forwards;
  display: flex;
  align-items: center;
`;

const StyledDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: ${({ $isOpen }) => ($isOpen ? "200px" : "0")};
  overflow: hidden;
  background-color: #ffffff;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  margin-top: 4px;
  z-index: 1000;
  transition: max-height 0.2s ease-in-out;
`;

const InputWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: #f8f9fa;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${COLOR_PRIMARY};
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;
